import React, { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { useAnchoredTooltip } from '../../hooks/useAnchoredTooltip';
import type { TooltipPosition } from './tooltipPlacement';

interface InfoTooltipProps {
  /** Longer help text (string or rich node). */
  content: string | React.ReactNode;
  className?: string;
  position?: TooltipPosition;
  /** Slightly faster than action tooltips — default 300ms. */
  delay?: number;
  size?: 'sm' | 'md';
  /** Custom trigger; defaults to Info icon. */
  children?: React.ReactNode;
  id?: string;
}

const SIZE_CLASSES = {
  sm: 'text-xs px-4 py-2.5 max-w-[320px]',
  md: 'text-sm px-5 py-3 max-w-[360px]',
} as const;

const ICON_SIZES = {
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
} as const;

const BUBBLE_HALF_WIDTH = {
  sm: 160,
  md: 180,
} as const;

/**
 * Informational tooltip (long copy / ℹ️).
 * Same placement engine as Tooltip; tap-to-toggle on touch; richer bubble.
 */
const InfoTooltip: React.FC<InfoTooltipProps> = ({
  content,
  className = '',
  position = 'bottom',
  delay = 300,
  size = 'md',
  children,
  id,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [exitStyle, setExitStyle] = useState<CSSProperties | undefined>();

  const {
    isVisible,
    tooltipId,
    wrapperRef,
    isTouch,
    shouldShowOnHover,
    showTooltip,
    hideTooltip,
    handleTriggerPointer,
    bubbleStyle,
  } = useAnchoredTooltip({
    position,
    delay,
    touchBehavior: 'toggle',
    flipNeed: size === 'md' ? 60 : 50,
    gap: 12,
    viewportPad: 12,
    bubbleHalfWidth: BUBBLE_HALF_WIDTH[size],
    id,
  });

  // Keep last position while fading out (coords clear immediately on hide)
  useEffect(() => {
    if (isVisible && bubbleStyle) {
      setExitStyle(bubbleStyle);
      setIsMounted(true);
      return;
    }
    if (!isMounted) return;
    const t = window.setTimeout(() => {
      setIsMounted(false);
      setExitStyle(undefined);
    }, 200);
    return () => window.clearTimeout(t);
  }, [isVisible, bubbleStyle, isMounted]);

  const portalStyle = isVisible ? bubbleStyle : exitStyle;

  const triggerElement = useMemo(() => {
    if (children) return children;
    return (
      <Info
        className={`${ICON_SIZES[size]} text-gray-400 hover:text-primary-600 cursor-help transition-colors duration-200`}
        aria-hidden="true"
      />
    );
  }, [children, size]);

  return (
    <>
      <div
        ref={wrapperRef}
        className={`inline-flex items-center ${className}`}
        onMouseEnter={shouldShowOnHover ? showTooltip : undefined}
        onMouseLeave={shouldShowOnHover ? hideTooltip : undefined}
        onFocus={shouldShowOnHover ? showTooltip : undefined}
        onBlur={shouldShowOnHover ? hideTooltip : undefined}
        onMouseDown={!isTouch ? handleTriggerPointer : undefined}
        onClick={handleTriggerPointer}
        onTouchStart={isTouch ? handleTriggerPointer : undefined}
        role="button"
        tabIndex={0}
        aria-describedby={isVisible ? tooltipId : undefined}
        aria-expanded={isVisible}
        aria-label={typeof content === 'string' ? content : undefined}
      >
        {triggerElement}
      </div>

      {isMounted && portalStyle && typeof document !== 'undefined' &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            className={`
              fixed z-[3000]
              bg-white/95 backdrop-blur-md
              border border-gray-200/80
              rounded-xl
              shadow-lg shadow-black/10
              ${SIZE_CLASSES[size]}
              pointer-events-none
              transition-opacity duration-200 ease-out
              ${isVisible ? 'opacity-100' : 'opacity-0'}
            `}
            style={portalStyle}
          >
            <span className="text-gray-700 leading-relaxed">{content}</span>
          </div>,
          document.body
        )}
    </>
  );
};

export default InfoTooltip;
